import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore, useCallback, useLayoutEffect } from "react";
import type { User } from "@shared/schema";

// Global session state management - shared across all useAuth instances
// This prevents the infinite loop where multiple components each have their own state
let sessionIndicator = typeof window !== 'undefined' 
  ? localStorage.getItem('has_session') === 'true' 
  : false;

// Track if we've received a 401 error - this MUST be global to prevent multiple fetches
// Also persisted to sessionStorage to survive hot reloads during dev
let hasAuthError = typeof window !== 'undefined'
  ? sessionStorage.getItem('auth_error_flag') === 'true'
  : false;

const listeners = new Set<() => void>();

function getSessionSnapshot(): boolean {
  // If we have an auth error, pretend we don't have a session
  // This immediately stops the query from firing
  return sessionIndicator && !hasAuthError;
}

function subscribeToSession(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

// Clear session indicator - called on 401
// Exported so other modules can use the same centralized function
export function clearSessionIndicator(): void {
  const needsUpdate = sessionIndicator || !hasAuthError;
  hasAuthError = true;
  sessionIndicator = false;
  localStorage.removeItem('has_session');
  sessionStorage.setItem('auth_error_flag', 'true'); // Persist across hot reloads
  
  if (needsUpdate) {
    console.log('[useAuth] Clearing session indicator due to 401');
    // Notify all subscribers synchronously
    listeners.forEach(listener => listener());
  }
}

// Set session indicator - called on successful login
export function setSessionIndicator(): void {
  const needsUpdate = !sessionIndicator || hasAuthError;
  sessionIndicator = true;
  hasAuthError = false; // Reset error state on new login
  localStorage.setItem('has_session', 'true');
  sessionStorage.removeItem('auth_error_flag'); // Clear error flag
  
  if (needsUpdate) {
    listeners.forEach(listener => listener());
  }
}

// Check if error is a 401 auth error
function is401Error(error: Error | null): boolean {
  if (!error) return false;
  const errorMsg = error.message || String(error);
  return errorMsg.includes('401') || errorMsg.includes('Unauthorized');
}

export function useAuth() {
  // Use useSyncExternalStore to share session state across all components
  // This ensures ALL useAuth hooks see the same value and update together
  // Note: getSessionSnapshot returns false if hasAuthError is true
  const hasSession = useSyncExternalStore(
    subscribeToSession,
    getSessionSnapshot,
    () => false // Server snapshot
  );
  
  const queryClient = useQueryClient();
  
  const { data: user, isLoading, error, isFetching, isError } = useQuery<User>({
    queryKey: ["/api/auth/user"],
    retry: false,
    // Only make the API call if we have a session indicator AND no auth error
    // hasSession already incorporates hasAuthError check via getSessionSnapshot
    enabled: hasSession,
    // Cache user data for 5 minutes - prevents infinite refetch loops
    // User data rarely changes and we manually invalidate on login/logout
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache a bit longer
    // Custom query function that handles 401s synchronously
    queryFn: async () => {
      const response = await fetch('/api/auth/user', { credentials: 'include' });
      if (response.status === 401) {
        // Clear session indicator IMMEDIATELY, before throwing
        // This prevents other components from firing the same query
        clearSessionIndicator();
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(`401: ${errorBody.message || 'Unauthorized'}`);
      }
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(`${response.status}: ${errorBody.message || response.statusText}`);
      }
      return response.json();
    },
  });

  // If we get a 401 error, also clear the query cache
  // This is a backup in case the queryFn's clearSessionIndicator didn't fully propagate
  useLayoutEffect(() => {
    if (isError && error && is401Error(error)) {
      clearSessionIndicator();
      queryClient.setQueryData(["/api/auth/user"], null);
    }
  }, [isError, error, queryClient]);

  // Provide a way to refresh session state (e.g., after login)
  const refreshSession = useCallback(() => {
    const hasIt = localStorage.getItem('has_session') === 'true';
    if (hasIt) {
      setSessionIndicator();
    } else {
      clearSessionIndicator();
    }
  }, []);

  return {
    user,
    // Not loading if we don't have a session (includes auth error case)
    isLoading: hasSession ? (isLoading || isFetching) : false,
    isAuthenticated: !!user,
    refreshSession,
  };
}
